import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import CompanyReport from './components/CompanyReport';
import AdminPage from './components/AdminPage';
import HubsLanding from './components/HubsLanding';
import HubPage from './components/HubPage';
import LoginPage from './components/LoginPage';
import PlansPage from './components/PlansPage';
import ProductPreviewPage from './components/ProductPreviewPage';
import ProfilePage from './components/ProfilePage';
import HelpPage from './components/HelpPage';
import Brand from './components/Brand';
import ResearchWorkspace from './components/ResearchWorkspace';
import LegalPage from './components/LegalPage';
import SiteFooter from './components/SiteFooter';
import { useAuth } from './contexts/AuthContext';
import { authApi } from './services/api';

const COMPANY_ROUTE = /^\/compan(?:y|ies)\/([A-Za-z0-9.-]{1,10})\/?$/;
const PUBLIC_COMPANY_ROUTE = /^\/p\/([A-Za-z0-9.-]{1,10})\/?$/;
const HUB_ROUTE = /^\/hubs\/([a-z0-9_-]{2,40})\/?$/;
const HUBS_ROUTE = /^\/hubs\/?$/;
const PLANS_ROUTE = /^\/plans\/?$/;
const PROFILE_ROUTE = /^\/profile\/?$/;
const HELP_ROUTE = /^\/help\/?$/;
const LEGAL_ROUTE = /^\/(terms|disclaimer)\/?$/;

const themeStorageKey = (user) => `ci:theme:${user?.user_id || user?.sub || user?.email || user?.username || 'account'}`;

const AppContent = () => {
  const { user, loading, logout, refreshSession } = useAuth();
  const [path, setPath] = useState(window.location.pathname);
  const [theme, setTheme] = useState('dark');
  const [preferenceError, setPreferenceError] = useState('');
  const preferenceRevision = useRef(0);
  const accountThemeKey = user ? themeStorageKey(user) : null;
  const currentUser = useRef(user);
  currentUser.current = user;

  useLayoutEffect(() => {
    const preference = accountThemeKey ? localStorage.getItem(accountThemeKey) : null;
    setTheme(preference === 'light' ? 'light' : 'dark');
    setPreferenceError('');
  }, [accountThemeKey]);

  useEffect(() => {
    if (!accountThemeKey) return undefined;
    let active = true;
    const requestRevision = preferenceRevision.current;
    const loadPreference = async () => {
      try {
        const result = await authApi.preferences();
        if (!active || requestRevision !== preferenceRevision.current) return;
        if (result.theme === 'dark' || result.theme === 'light') {
          localStorage.setItem(accountThemeKey, result.theme);
          setTheme(result.theme);
          setPreferenceError('');
          return;
        }
        const cachedTheme = localStorage.getItem(accountThemeKey);
        if (cachedTheme === 'dark' || cachedTheme === 'light') {
          await authApi.savePreferences({ theme: cachedTheme });
          if (!active || requestRevision !== preferenceRevision.current) return;
          setTheme(cachedTheme);
        }
        setPreferenceError('');
      } catch (err) {
        if (active) setPreferenceError(err.message);
      }
    };
    loadPreference();
    return () => { active = false; };
  }, [accountThemeKey]);

  useLayoutEffect(() => {
    document.documentElement.dataset.theme = theme;
    let themeColor = document.querySelector('meta[name="theme-color"]');
    if (!themeColor) {
      themeColor = document.createElement('meta');
      themeColor.name = 'theme-color';
      document.head.appendChild(themeColor);
    }
    themeColor.content = theme === 'light' ? '#f6f6f6' : '#0b0b0b';
  }, [theme]);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to) => {
    window.history.pushState({}, '', to);
    setPath(new URL(to, window.location.origin).pathname);
    window.scrollTo(0, 0);
  }, []);

  const legalMatch = path.match(LEGAL_ROUTE);
  if (legalMatch) return <LegalPage kind={legalMatch[1]} user={user} onLogout={logout} onOpenProfile={() => navigate('/profile')} />;
  if (loading) return <div className="loading-screen"><Brand /><p>Loading your workspace...</p></div>;
  const publicMatch = path.match(PUBLIC_COMPANY_ROUTE);
  if (publicMatch) return <CompanyReport ticker={publicMatch[1].toUpperCase()} publicMode />;
  if (/^\/preview\/?$/.test(path)) return <ProductPreviewPage user={user} onLogout={logout} onOpenProfile={() => navigate('/profile')} />;
  if (PLANS_ROUTE.test(path)) return <PlansPage user={user} onBack={() => navigate('/')} onLogout={logout} onOpenProfile={() => navigate('/profile')} onRefresh={refreshSession} />;
  const hubMatch = path.match(HUB_ROUTE);
  if (HUBS_ROUTE.test(path)) return <HubsLanding user={user} publicMode={!user} onBack={() => navigate('/')} onLogout={logout} onOpenProfile={() => navigate('/profile')} />;
  if (hubMatch) return <HubPage key={hubMatch[1]} hubId={hubMatch[1]} user={user} publicMode={!user} onBack={() => navigate('/hubs')} onLogout={logout} onOpenProfile={() => navigate('/profile')} onOpenCompany={(ticker, product) => navigate(`/company/${encodeURIComponent(ticker)}${product ? `?product=${encodeURIComponent(product)}` : ''}`)} />;
  if (!user) return <LoginPage />;
  if (HELP_ROUTE.test(path)) return <HelpPage key={user.user_id || user.email || user.username} user={user} onBack={() => navigate('/')} onLogout={logout} />;
  const isAdmin = user.permissions?.includes('admin') || user.email?.toLowerCase() === 'greg@hffinancial.com';
  const isEmployee = user.plan === 'employee' && !isAdmin;
  const match = path.match(COMPANY_ROUTE);
  if (PROFILE_ROUTE.test(path)) {
    return <ProfilePage user={user} theme={theme} onThemeChange={(nextTheme) => {
      const requestRevision = ++preferenceRevision.current;
      const requestUser = user;
      const requestThemeKey = accountThemeKey;
      setPreferenceError('');
      authApi.savePreferences({ theme: nextTheme }).then(() => {
        if (currentUser.current !== requestUser || preferenceRevision.current !== requestRevision) return;
        localStorage.setItem(requestThemeKey, nextTheme);
        setTheme(nextTheme);
      }).catch((err) => {
        if (currentUser.current === requestUser && preferenceRevision.current === requestRevision) {
          setPreferenceError(err.message);
        }
      });
    }} preferenceError={preferenceError} onBack={() => navigate('/')} onLogout={logout} onDeleted={() => {
      ++preferenceRevision.current;
      localStorage.removeItem(accountThemeKey);
      logout();
    }} />;
  }
  if (path === '/admin' && (isAdmin || isEmployee)) {
    return <AdminPage user={user} employeeAccess={isEmployee} onLogout={logout} onBack={() => navigate('/')} onOpenProfile={() => navigate('/profile')} />;
  }
  if (match) return <CompanyReport ticker={match[1].toUpperCase()} user={user} onBack={() => navigate('/')} onLogout={logout} onOpenProfile={() => navigate('/profile')} onOpenCompany={(ticker, product) => navigate(`/company/${encodeURIComponent(ticker)}${product ? `?product=${encodeURIComponent(product)}` : ''}`)} />;
  return <ResearchWorkspace user={user} onLogout={logout} onOpenAdmin={() => navigate('/admin')} onOpenHubs={() => navigate('/hubs')} onOpenPlans={() => navigate('/plans')} onOpenProfile={() => navigate('/profile')} onOpenCompany={(ticker, product) => navigate(`/company/${encodeURIComponent(ticker)}${product ? `?product=${encodeURIComponent(product)}` : ''}`)} />;
};

const App = () => (
  <div className="site-layout">
    <AppContent />
    <SiteFooter />
  </div>
);

export default App;
